import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Shield, Lock, Key, Check, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SettingsPage() {
  const { user } = useAuth();
  const [hasPin, setHasPin] = useState(false);
  const [showSetPin, setShowSetPin] = useState(false);
  const [pin, setPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [saving, setSaving] = useState(false);

  const fetchStatus = useCallback(async () => {
    if (!user) return;
    try {
      const { data } = await axios.get(`${API}/pin/status`, { withCredentials: true });
      setHasPin(data.has_pin);
    } catch { /* ignore */ }
  }, [user]);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  const handleSetPin = async () => {
    if (pin.length !== 4 || !/^\d{4}$/.test(pin)) { toast.error('PIN must be exactly 4 digits'); return; }
    if (pin !== confirmPin) { toast.error('PINs do not match'); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/pin/set`, { pin }, { withCredentials: true });
      toast.success('Parental PIN set!');
      setHasPin(true);
      setShowSetPin(false);
      setPin(''); setConfirmPin('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to set PIN');
    }
    setSaving(false);
  };

  if (!user) return (
    <div className="page-content min-h-[60vh] flex items-center justify-center">
      <div className="text-center">
        <Lock size={48} className="text-[#FF3860] mx-auto mb-4" strokeWidth={1.5} />
        <p className="font-['Fredoka'] text-2xl text-[#0A0A0A] mb-2">Sign in to access settings</p>
        <Link to="/login" className="neo-btn inline-block px-6 py-2 rounded-xl bg-[#FF3860] text-white text-sm">Sign In</Link>
      </div>
    </div>
  );

  return (
    <div className="page-content" data-testid="settings-page">
      <div className="max-w-2xl mx-auto px-6 md:px-8 py-8">
        <h1 className="font-['Fredoka'] text-4xl sm:text-5xl font-black text-[#0A0A0A] tracking-tight mb-8" data-testid="settings-title">
          Settings
        </h1>

        {/* Parental PIN */}
        <div className="neo-card p-6 bg-white mb-6" data-testid="pin-settings">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-lg border-2 border-[#0A0A0A] bg-[#4AC6FF] flex items-center justify-center shadow-[2px_2px_0px_0px_rgba(10,10,10,1)]">
              <Shield size={20} strokeWidth={2.5} color="white" />
            </div>
            <div>
              <h2 className="font-['Fredoka'] text-xl font-bold text-[#0A0A0A]">Parental PIN</h2>
              <p className="text-sm text-[#4B5563] font-['Nunito']">Protect admin and parent-only areas</p>
            </div>
          </div>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {hasPin ? (
                <span className="flex items-center gap-1 text-sm font-bold text-green-700 bg-green-50 px-3 py-1 rounded-lg border border-green-200">
                  <Check size={14} strokeWidth={3} /> PIN Active
                </span>
              ) : (
                <span className="text-sm text-[#FF6B00] font-bold font-['Nunito']">No PIN set - settings are unprotected</span>
              )}
            </div>
            <button
              onClick={() => setShowSetPin(true)}
              className="neo-btn flex items-center gap-1 px-4 py-2 rounded-lg bg-[#4AC6FF] text-[#0A0A0A] text-sm"
              data-testid="set-pin-btn"
            >
              <Key size={14} strokeWidth={2.5} /> {hasPin ? 'Change PIN' : 'Set PIN'}
            </button>
          </div>
        </div>

        {/* Quick Links */}
        <div className="space-y-3">
          {[
            { label: 'Kid Profiles', desc: 'Manage child profiles and favorites', to: '/profiles', color: '#FF3860' },
            { label: 'Support Us', desc: 'Leave a voluntary tip', to: '/donate', color: '#FFDE00' },
            { label: 'Help & FAQ', desc: 'Common questions and setup guides', to: '/faq', color: '#4AC6FF' },
          ].map(item => (
            <Link key={item.label} to={item.to} className="neo-card flex items-center justify-between p-4 bg-white group" data-testid={`settings-link-${item.label.toLowerCase().replace(/ /g, '-')}`}>
              <div>
                <h3 className="font-['Fredoka'] font-bold text-[#0A0A0A] group-hover:text-[#FF3860] transition-colors">{item.label}</h3>
                <p className="text-xs text-[#4B5563] font-['Nunito']">{item.desc}</p>
              </div>
              <ChevronRight size={18} strokeWidth={2.5} className="text-[#4B5563]" />
            </Link>
          ))}
        </div>

        {/* Set PIN Dialog */}
        <Dialog open={showSetPin} onOpenChange={setShowSetPin}>
          <DialogContent className="border-2 border-[#0A0A0A] shadow-[6px_6px_0px_0px_rgba(10,10,10,1)] max-w-sm" data-testid="set-pin-dialog">
            <DialogHeader>
              <DialogTitle className="font-['Fredoka'] text-xl flex items-center gap-2">
                <Shield size={20} strokeWidth={2.5} className="text-[#4AC6FF]" /> {hasPin ? 'Change' : 'Set'} Parental PIN
              </DialogTitle>
            </DialogHeader>
            <p className="text-sm text-[#4B5563] font-['Nunito'] mb-4">
              Set a 4-digit PIN to protect admin settings and account changes. Kids can browse and enjoy shows freely.
            </p>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-bold text-[#0A0A0A] font-['Fredoka'] mb-1">Enter 4-Digit PIN</label>
                <input
                  type="password"
                  inputMode="numeric"
                  maxLength={4}
                  value={pin}
                  onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
                  placeholder="----"
                  className="w-full text-center text-2xl tracking-[0.5em] px-4 py-3 border-2 border-[#0A0A0A] rounded-lg font-['IBM_Plex_Mono'] focus:outline-none focus:ring-2 focus:ring-[#4AC6FF]"
                  data-testid="pin-input"
                />
              </div>
              <div>
                <label className="block text-sm font-bold text-[#0A0A0A] font-['Fredoka'] mb-1">Confirm PIN</label>
                <input
                  type="password"
                  inputMode="numeric"
                  maxLength={4}
                  value={confirmPin}
                  onChange={(e) => setConfirmPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
                  placeholder="----"
                  className="w-full text-center text-2xl tracking-[0.5em] px-4 py-3 border-2 border-[#0A0A0A] rounded-lg font-['IBM_Plex_Mono'] focus:outline-none focus:ring-2 focus:ring-[#4AC6FF]"
                  data-testid="pin-confirm-input"
                />
              </div>
            </div>
            <DialogFooter className="mt-4">
              <button onClick={() => { setShowSetPin(false); setPin(''); setConfirmPin(''); }} className="neo-btn px-4 py-2 rounded-lg bg-white text-sm">Cancel</button>
              <button onClick={handleSetPin} disabled={saving || pin.length !== 4} className="neo-btn px-4 py-2 rounded-lg bg-[#4AC6FF] text-[#0A0A0A] text-sm disabled:opacity-50" data-testid="save-pin-btn">
                {saving ? 'Saving...' : 'Save PIN'}
              </button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
